import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ApolloConsumer } from '@apollo/client';
import { ArticleGrid } from '@fuww/components/src/Article';
import { GridCell } from '@fuww/components/src/Grid';
import {
  DesktopAdUnitContainer,
  TabletAdUnitContainer,
  MobileAdUnitContainer,
} from '@fuww/components/src/Advertisement/AdUnitContainer';
import dynamic from 'next/dynamic';
import Head from '../../Head';
import withQuery from '../../../lib/withQuery';
import throw404 from '../../../lib/throw404';
import getArticleJsonLdData from '../../../lib/getArticleJsonLdData';
import getArticleField from '../../../lib/queries/news/getArticleField';
import getArticleQuery from '../../../lib/queries/news/getArticleQuery';
import getArticlesField from '../../../lib/queries/news/getArticlesField';
import getArticleSubjectType from '../../../lib/getArticleSubjectType';
import { articleImageConfigurations } from '../../../lib/imageConfigurations';
import getIncreaseHitsMutation from
'../../../lib/queries/news/getIncreaseHitsMutation';
import { metaImageDimensions, sponsorIds } from '../../../lib/constants';
import BreadCrumbs from './BreadCrumb';
import Main from './Main';
import RelatedNews from './RelatedNews';
import getDataLayerVariables from './getDataLayerVariables';
import loading from '../../../lib/loading';
import SiteContext from '../../SiteContext';
import InView from '../../InView';
import AdUnit from '../../../lib/adunit';
import transformKeywords from '../../../lib/transformKeywords';
import getSourceSet from '../../../lib/getSourceSet';

const DynamicSideBar = dynamic(() => import('./SideBar'), {
  ssr: false,
  loading,
});

export const ArticleWithLayout = ({
  data,
  variables: { imageConfigurations },
  field,
  ArticleComponent,
  apolloClient,
}) => {
  const {
    prefix, hasLocalNews, url, origin, newsboard,
  } = useContext(SiteContext);
  const article = data[field];

  useEffect(() => {
    if (article) {
      apolloClient.mutate({
        mutation: getIncreaseHitsMutation(prefix, hasLocalNews),
        variables: {
          id: Number(article.id),
          newsboard,
        },
      });
    }
  }, [article, apolloClient, hasLocalNews, prefix, newsboard]);

  if (!article) return throw404();

  const {
    id, title, creator, keywords,
    createdAt, description, imageUrls: originalImageUrls,
  } = article;
  const [metaImageUrl, ...imageUrls] = originalImageUrls;

  const articlesField = getArticlesField(prefix, hasLocalNews);

  const { name: author, id: authorId } = creator || {};

  const robotsMeta = sponsorIds[articlesField] === authorId
    ? 'index, nofollow' : null;

  const dataLayerVariables = getDataLayerVariables(article);
  const transformedKeywords = transformKeywords(keywords);

  const sourceSet = getSourceSet(imageConfigurations.slice(1), imageUrls);

  return (
    <>
      <Head
        title={title}
        description={description}
        imageUrl={metaImageUrl}
        imageDimensions={metaImageDimensions}
        createdAt={createdAt}
        author={author}
        robotsMeta={robotsMeta}
        jsonLdData={getArticleJsonLdData(article, url, origin)}
        subjectType={getArticleSubjectType(prefix, hasLocalNews)}
        subjectId={id}
        payload={{ newsboard }}
        addCanonical
      >
        <script
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer = window.dataLayer || []; \
  window.dataLayer.push(${JSON.stringify(dataLayerVariables)});`,
          }}
        />
      </Head>
      <ArticleGrid>
        <GridCell
          sm={12}
          lg={12}
          padding="0 0 20px 12px"
        >
          <BreadCrumbs article={article} />
        </GridCell>
        <GridCell
          sm={12}
          lg={8}
          tag="article"
          padding="0px 8px 0"
        >
          <Main
            article={article}
            ArticleComponent={ArticleComponent}
            srcSet={sourceSet}
          />

          <MobileAdUnitContainer>
            <AdUnit
              adUnit="mobile_article_bottom"
              sizes={[[1, 1], [320, 240], [300, 250]]}
              minHeight="90px"
            />
          </MobileAdUnitContainer>

          <TabletAdUnitContainer>
            <AdUnit
              adUnit="tablet_article_bottom"
              sizes={[[1, 1], [300, 250]]}
              minHeight="90px"
            />
          </TabletAdUnitContainer>
          <DesktopAdUnitContainer>
            <AdUnit
              adUnit="article_bottom"
              sizes={[[1, 1], [300, 250]]}
              minHeight="90px"
            />
          </DesktopAdUnitContainer>
          <InView>
            <RelatedNews
              keywords={transformedKeywords}
              articleId={id}
              showCategory
              showImage
            />
          </InView>
        </GridCell>
        <GridCell
          sm={12}
          lg={4}
          tag="aside"
          padding="0px 8px 0"
        >
          <InView>
            <DynamicSideBar
              keywords={keywords}
              articlesField={articlesField}
              articleId={id}
            />
          </InView>
        </GridCell>
      </ArticleGrid>
    </>
  );
};

export const articlePropertyTypes = {
  variables: PropTypes.shape({
    imageConfigurations: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  field: PropTypes.string.isRequired,
};

ArticleWithLayout.propTypes = {
  ...articlePropertyTypes,
  data: PropTypes.shape({}),
  ArticleComponent: PropTypes.elementType,
  apolloClient: PropTypes.shape({
    mutate: PropTypes.func,
  }).isRequired,
};
ArticleWithLayout.defaultProps = {
  data: {},
  ArticleComponent: undefined,
};

const ArticleWithLayoutWithApolloClient = React.forwardRef(
  (properties, reference) => (
    <ApolloConsumer>
      {(client) => (
        <ArticleWithLayout
          {...properties}
          apolloClient={client}
          ref={reference}
        />
      )}
    </ApolloConsumer>
  ),
);

const ArticleWithQuery = withQuery(ArticleWithLayoutWithApolloClient);

const Article = ({
  id,
  path,
  ArticleComponent,
}) => {
  const { prefix, hasLocalNews } = useContext(SiteContext);
  const field = getArticleField(prefix, hasLocalNews, !id);

  return (
    <ArticleWithQuery
      query={getArticleQuery(field, !id)}
      field={field}
      variables={{
        ...id ? { id } : { path },
        imageConfigurations: articleImageConfigurations,
      }}
      errorMessage="Error loading this news article..."
      ArticleComponent={ArticleComponent}
    />
  );
};

Article.propTypes = {
  id: PropTypes.number,
  path: PropTypes.string,
  ArticleComponent: PropTypes.elementType,
};

Article.defaultProps = {
  id: null,
  path: null,
  ArticleComponent: undefined,
};

export default Article;
